import { useForm, type Control, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSubmitRaiserApplication } from "@workspace/api-client-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@workspace/ui-web/components/ui/button";
import { Input } from "@workspace/ui-web/components/ui/input";
import { Textarea } from "@workspace/ui-web/components/ui/textarea";
import { Checkbox } from "@workspace/ui-web/components/ui/checkbox";
import {
  RadioGroup,
  RadioGroupItem,
} from "@workspace/ui-web/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui-web/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui-web/components/ui/form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import { useToast } from "@workspace/ui-web/hooks/use-toast";
import { Loader2, CheckCircle2, PawPrint } from "lucide-react";
import { useSeo } from "@/lib/seo";

const yesNo = z.enum(["yes", "no"], {
  errorMap: () => ({ message: "Please choose yes or no" }),
});
const optionalYesNo = z.enum(["yes", "no"]).or(z.literal("")).optional();

const applySchema = z
  .object({
    fullName: z.string().min(1, "Please enter your full name"),
    address: z.string().min(1, "Please enter your street address"),
    city: z.string().min(1, "Please enter your city"),
    state: z.string().min(1, "Please enter your state"),
    zip: z.string().min(1, "Please enter your ZIP code"),
    daytimePhone: z.string().optional(),
    eveningPhone: z.string().optional(),
    cellPhone: z.string().min(1, "Please enter your cell phone"),
    email: z
      .string()
      .min(1, "Please enter your email")
      .email("Please enter a valid email"),
    recoveryConnection: z.string().optional(),
    howHeard: z.string().min(1, "Please tell us how you heard about us"),
    whyRaiser: z
      .string()
      .min(1, "Please tell us why you want to be a puppy raiser"),

    primaryHandlerName: z.string().min(1, "Please enter a name"),
    primaryHandlerAge: z.string().min(1, "Required"),
    primaryHandlerOccupation: z.string().min(1, "Required"),
    secondaryHandlerName: z.string().optional(),
    secondaryHandlerAge: z.string().optional(),
    secondaryHandlerOccupation: z.string().optional(),
    householdMembers: z.string().optional(),

    typicalWeekday: z.string().min(1, "Please describe a typical weekday"),
    typicalWeekend: z.string().min(1, "Please describe a typical weekend"),
    ownedDogsBefore: yesNo,
    ownedDogsDetails: z.string().optional(),
    raisedPuppy: optionalYesNo,
    pottyTrainedPuppy: optionalYesNo,
    crateTrainedPuppy: optionalYesNo,
    currentPets: yesNo,
    currentPetsDetails: z.string().optional(),
    petsSpayedNeutered: optionalYesNo,
    petsVaccinationProof: optionalYesNo,
    vetName: z.string().optional(),
    vetPhone: z.string().optional(),
    petProblemBehaviors: optionalYesNo,
    petProblemBehaviorsDetails: z.string().optional(),
    trainingClasses: optionalYesNo,
    trainingClassesDetails: z.string().optional(),

    residenceType: z.enum(
      ["house", "townhouse", "apartment_condo", "dorm", "other"],
      { errorMap: () => ({ message: "Please choose your residence type" }) },
    ),
    residenceTypeOther: z.string().optional(),
    ownOrRent: z.enum(["own", "rent"], {
      errorMap: () => ({ message: "Please choose one" }),
    }),
    landlordPermission: optionalYesNo,
    landlordName: z.string().optional(),
    landlordPhone: z.string().optional(),
    hasYard: yesNo,
    yardFenced: optionalYesNo,
    fenceDescription: z.string().optional(),
    hasPool: yesNo,
    poolFenced: optionalYesNo,
    neighborhoodDogInteraction: z.string().optional(),

    acceptDamageRisk: yesNo,
    acknowledgeAccidents: yesNo,
    planWorkSchool: z.enum(["work", "school", "both", "neither"], {
      errorMap: () => ({ message: "Please choose one" }),
    }),
    workSchoolPermission: optionalYesNo,
    workSchoolDaysHours: z.string().optional(),
    workSchoolLocation: z.string().optional(),
    workSchoolEnvironment: z.string().optional(),
    aloneHours: z.string().min(1, "Required"),
    aloneLocation: z.string().optional(),
    situationsNotTake: z.string().optional(),
    sleepingArrangements: z.string().min(1, "Required"),
    exercisePlan: z.string().min(1, "Required"),
    publicOutingsCommitment: yesNo,
    publicOutingsExplain: z.string().optional(),
    smokes: yesNo,
    refrainFromSmoking: optionalYesNo,
    commitTrainingSessions: yesNo,
    commitReports: yesNo,
    consentHomeVisits: yesNo,
    anyGenderBreed: yesNo,
    anyGenderBreedExplain: z.string().optional(),

    referenceName: z.string().min(1, "Please enter a reference name"),
    referenceRelationship: z.string().min(1, "Required"),
    referencePhone: z.string().min(1, "Required"),
    referenceEmail: z.string().optional(),
    referenceAddress: z.string().optional(),

    agreeEmergencyCare: z.boolean().refine((v) => v, {
      message: "This acknowledgment is required",
    }),
    agreeRiskAcknowledgment: z.boolean().refine((v) => v, {
      message: "This acknowledgment is required",
    }),
    agreeTruthful: z.boolean().refine((v) => v, {
      message: "This acknowledgment is required",
    }),
    signatureName: z.string().min(1, "Please type your full name to sign"),

    website: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.ownOrRent === "rent" && !data.landlordPermission) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["landlordPermission"],
        message: "Please choose yes or no",
      });
    }
    if (data.smokes === "yes" && !data.refrainFromSmoking) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["refrainFromSmoking"],
        message: "Please choose yes or no",
      });
    }
  });

type ApplyFormValues = z.infer<typeof applySchema>;

const toBool = (v: "yes" | "no") => v === "yes";
const toOptBool = (v: "yes" | "no" | "" | undefined) =>
  v === "yes" ? true : v === "no" ? false : undefined;
const optText = (v: string | undefined) => {
  const t = v?.trim();
  return t ? t : undefined;
};

const RESPONSIBILITIES = [
  "Care for the dog in a safe and loving home for the periods of time we arrange together.",
  "Transport the dog to and from our training site as requested so it can train with our team.",
  "Provide food, water, a toileting area, and appropriate toys while the dog is in your care.",
  "Take the dog on public outings to expose it to new environments, people, and experiences.",
  "Reinforce the dog's training and, in some cases, help teach new skills.",
  "Attend raiser training meetings and submit regular progress reports.",
  "Obtain veterinary care for the dog when needed and as requested.",
];

const QUALIFICATIONS = [
  "The primary handler must have permission to bring the dog to work or school, or have someone available for the puppy at least every four hours.",
  "The primary handler should be able to drive and have access to a car.",
  "You must own your home or have written permission to keep a dog where you live.",
  "Your home must be safe, able to be puppy-proofed, and have a safe outdoor area for potty training.",
  "Handlers must be non-smokers or willing to refrain from smoking around the dog, including in the house.",
  "No dogs under two years old in the home, and no dogs with aggression or other behavior issues.",
  "All handlers must be physically and mentally able to train, carry, and manage a puppy.",
  "Everyone in the household must be willing to use positive training methods and commit to socialization and potty training.",
  "Everyone in the household should be committed to our mission of providing highly trained service dogs for people in recovery.",
  "Experience with dogs is preferred but not required.",
];

function TextF({
  control,
  name,
  label,
  placeholder,
  type = "text",
}: {
  control: Control<ApplyFormValues>;
  name: FieldPath<ApplyFormValues>;
  label: string;
  placeholder?: string;
  type?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type={type}
              placeholder={placeholder}
              {...field}
              value={typeof field.value === "string" ? field.value : ""}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function AreaF({
  control,
  name,
  label,
  placeholder,
  rows,
}: {
  control: Control<ApplyFormValues>;
  name: FieldPath<ApplyFormValues>;
  label: string;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea
              placeholder={placeholder}
              className={rows && rows > 3 ? "min-h-[120px]" : "min-h-[80px]"}
              {...field}
              value={typeof field.value === "string" ? field.value : ""}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function YesNoF({
  control,
  name,
  label,
}: {
  control: Control<ApplyFormValues>;
  name: FieldPath<ApplyFormValues>;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-2">
          <FormLabel className="leading-snug">{label}</FormLabel>
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              value={typeof field.value === "string" ? field.value : ""}
              className="flex gap-6"
            >
              <FormItem className="flex items-center space-x-2 space-y-0">
                <FormControl>
                  <RadioGroupItem value="yes" />
                </FormControl>
                <FormLabel className="font-normal">Yes</FormLabel>
              </FormItem>
              <FormItem className="flex items-center space-x-2 space-y-0">
                <FormControl>
                  <RadioGroupItem value="no" />
                </FormControl>
                <FormLabel className="font-normal">No</FormLabel>
              </FormItem>
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{title}</CardTitle>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-6">{children}</CardContent>
    </Card>
  );
}

export default function Apply() {
  useSeo({
    title: "Puppy Raiser Application | Greater Hope Service Dogs",
    description:
      "Apply to become a volunteer puppy raiser for Greater Hope Service Dogs and help socialize a future service dog for someone in recovery.",
    path: "/apply",
  });
  const { toast } = useToast();
  const submitApplication = useSubmitRaiserApplication();

  const form = useForm<ApplyFormValues>({
    resolver: zodResolver(applySchema),
    defaultValues: {
      fullName: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      daytimePhone: "",
      eveningPhone: "",
      cellPhone: "",
      email: "",
      recoveryConnection: "",
      howHeard: "",
      whyRaiser: "",
      primaryHandlerName: "",
      primaryHandlerAge: "",
      primaryHandlerOccupation: "",
      secondaryHandlerName: "",
      secondaryHandlerAge: "",
      secondaryHandlerOccupation: "",
      householdMembers: "",
      typicalWeekday: "",
      typicalWeekend: "",
      ownedDogsDetails: "",
      raisedPuppy: "",
      pottyTrainedPuppy: "",
      crateTrainedPuppy: "",
      currentPetsDetails: "",
      petsSpayedNeutered: "",
      petsVaccinationProof: "",
      vetName: "",
      vetPhone: "",
      petProblemBehaviors: "",
      petProblemBehaviorsDetails: "",
      trainingClasses: "",
      trainingClassesDetails: "",
      residenceTypeOther: "",
      landlordPermission: "",
      landlordName: "",
      landlordPhone: "",
      yardFenced: "",
      fenceDescription: "",
      poolFenced: "",
      neighborhoodDogInteraction: "",
      workSchoolPermission: "",
      workSchoolDaysHours: "",
      workSchoolLocation: "",
      workSchoolEnvironment: "",
      aloneHours: "",
      aloneLocation: "",
      situationsNotTake: "",
      sleepingArrangements: "",
      exercisePlan: "",
      publicOutingsExplain: "",
      refrainFromSmoking: "",
      anyGenderBreedExplain: "",
      referenceName: "",
      referenceRelationship: "",
      referencePhone: "",
      referenceEmail: "",
      referenceAddress: "",
      agreeEmergencyCare: false,
      agreeRiskAcknowledgment: false,
      agreeTruthful: false,
      signatureName: "",
      website: "",
    },
  });

  const watch = form.watch;
  const ownedDogsBefore = watch("ownedDogsBefore");
  const raisedPuppy = watch("raisedPuppy");
  const currentPets = watch("currentPets");
  const petProblemBehaviors = watch("petProblemBehaviors");
  const trainingClasses = watch("trainingClasses");
  const residenceType = watch("residenceType");
  const ownOrRent = watch("ownOrRent");
  const hasYard = watch("hasYard");
  const yardFenced = watch("yardFenced");
  const hasPool = watch("hasPool");
  const planWorkSchool = watch("planWorkSchool");
  const publicOutings = watch("publicOutingsCommitment");
  const smokes = watch("smokes");
  const anyGenderBreed = watch("anyGenderBreed");

  const isSubmitted = submitApplication.isSuccess;

  const onSubmit = (data: ApplyFormValues) => {
    submitApplication.mutate(
      {
        data: {
          fullName: data.fullName.trim(),
          address: data.address.trim(),
          city: data.city.trim(),
          state: data.state.trim(),
          zip: data.zip.trim(),
          daytimePhone: optText(data.daytimePhone),
          eveningPhone: optText(data.eveningPhone),
          cellPhone: data.cellPhone.trim(),
          email: data.email.trim(),
          recoveryConnection: optText(data.recoveryConnection),
          howHeard: data.howHeard.trim(),
          whyRaiser: data.whyRaiser.trim(),
          primaryHandlerName: data.primaryHandlerName.trim(),
          primaryHandlerAge: data.primaryHandlerAge.trim(),
          primaryHandlerOccupation: data.primaryHandlerOccupation.trim(),
          secondaryHandlerName: optText(data.secondaryHandlerName),
          secondaryHandlerAge: optText(data.secondaryHandlerAge),
          secondaryHandlerOccupation: optText(data.secondaryHandlerOccupation),
          householdMembers: optText(data.householdMembers),
          typicalWeekday: data.typicalWeekday.trim(),
          typicalWeekend: data.typicalWeekend.trim(),
          ownedDogsBefore: toBool(data.ownedDogsBefore),
          ownedDogsDetails: optText(data.ownedDogsDetails),
          raisedPuppy: toOptBool(data.raisedPuppy),
          pottyTrainedPuppy: toOptBool(data.pottyTrainedPuppy),
          crateTrainedPuppy: toOptBool(data.crateTrainedPuppy),
          currentPets: toBool(data.currentPets),
          currentPetsDetails: optText(data.currentPetsDetails),
          petsSpayedNeutered: toOptBool(data.petsSpayedNeutered),
          petsVaccinationProof: toOptBool(data.petsVaccinationProof),
          vetName: optText(data.vetName),
          vetPhone: optText(data.vetPhone),
          petProblemBehaviors: toOptBool(data.petProblemBehaviors),
          petProblemBehaviorsDetails: optText(data.petProblemBehaviorsDetails),
          trainingClasses: toOptBool(data.trainingClasses),
          trainingClassesDetails: optText(data.trainingClassesDetails),
          residenceType: data.residenceType,
          residenceTypeOther: optText(data.residenceTypeOther),
          ownOrRent: data.ownOrRent,
          landlordPermission: toOptBool(data.landlordPermission),
          landlordName: optText(data.landlordName),
          landlordPhone: optText(data.landlordPhone),
          hasYard: toBool(data.hasYard),
          yardFenced: toOptBool(data.yardFenced),
          fenceDescription: optText(data.fenceDescription),
          hasPool: toBool(data.hasPool),
          poolFenced: toOptBool(data.poolFenced),
          neighborhoodDogInteraction: optText(data.neighborhoodDogInteraction),
          acceptDamageRisk: toBool(data.acceptDamageRisk),
          acknowledgeAccidents: toBool(data.acknowledgeAccidents),
          planWorkSchool: data.planWorkSchool,
          workSchoolPermission: toOptBool(data.workSchoolPermission),
          workSchoolDaysHours: optText(data.workSchoolDaysHours),
          workSchoolLocation: optText(data.workSchoolLocation),
          workSchoolEnvironment: optText(data.workSchoolEnvironment),
          aloneHours: data.aloneHours.trim(),
          aloneLocation: optText(data.aloneLocation),
          situationsNotTake: optText(data.situationsNotTake),
          sleepingArrangements: data.sleepingArrangements.trim(),
          exercisePlan: data.exercisePlan.trim(),
          publicOutingsCommitment: toBool(data.publicOutingsCommitment),
          publicOutingsExplain: optText(data.publicOutingsExplain),
          smokes: toBool(data.smokes),
          refrainFromSmoking: toOptBool(data.refrainFromSmoking),
          commitTrainingSessions: toBool(data.commitTrainingSessions),
          commitReports: toBool(data.commitReports),
          consentHomeVisits: toBool(data.consentHomeVisits),
          anyGenderBreed: toBool(data.anyGenderBreed),
          anyGenderBreedExplain: optText(data.anyGenderBreedExplain),
          referenceName: data.referenceName.trim(),
          referenceRelationship: data.referenceRelationship.trim(),
          referencePhone: data.referencePhone.trim(),
          referenceEmail: optText(data.referenceEmail),
          referenceAddress: optText(data.referenceAddress),
          agreeEmergencyCare: data.agreeEmergencyCare,
          agreeRiskAcknowledgment: data.agreeRiskAcknowledgment,
          agreeTruthful: data.agreeTruthful,
          signatureName: data.signatureName.trim(),
          website: optText(data.website),
        },
      },
      {
        onSuccess: () => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
        onError: () => {
          toast({
            title: "Something went wrong",
            description:
              "We could not submit your application. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  const onInvalid = () => {
    toast({
      title: "Please check the form",
      description: "Some required questions still need an answer.",
      variant: "destructive",
    });
  };

  const control = form.control;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="bg-primary text-primary-foreground py-16">
          <div className="container px-4 md:px-8 max-w-4xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Puppy Raiser Application
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
              Open your home to a future service dog and help us change a life
              in recovery. Tell us about yourself and your household below.
            </p>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container px-4 md:px-8 max-w-3xl mx-auto space-y-8">
            {isSubmitted ? (
              <Card>
                <CardContent className="py-14 text-center space-y-4">
                  <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold text-foreground">
                    Application received
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Thank you for applying to be a puppy raiser. Our team will
                    review your application and reach out to you soon.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <PawPrint className="h-5 w-5 text-primary" /> About the
                      Role
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 text-sm text-foreground">
                    <p>
                      Puppy raisers play a crucial role in the life of a service
                      dog by socializing the dog to new environments,
                      experiences, and people. The experiences a service dog has
                      early in life are critical to its later development and
                      behavior. Raising a puppy is fun and rewarding, and it
                      helps us further our mission of providing highly trained
                      service dogs to people in recovery.
                    </p>
                    <p>
                      Puppy raisers are volunteers. Greater Hope Service Dogs
                      provides some of the items needed to raise a puppy in your
                      home, such as a leash, harness, and food. Other items you
                      purchase for the puppy are considered a tax-deductible
                      in-kind donation.
                    </p>
                    <div>
                      <h3 className="font-semibold mb-2">
                        Raiser responsibilities
                      </h3>
                      <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                        {RESPONSIBILITIES.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Qualifications</h3>
                      <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                        {QUALIFICATIONS.map((q) => (
                          <li key={q}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                    className="space-y-8"
                  >
                    {/* Honeypot: hidden from real users, only bots fill this in */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "-9999px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                      }}
                    >
                      <label htmlFor="apply-website">
                        Leave this field empty
                      </label>
                      <input
                        id="apply-website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        {...form.register("website")}
                      />
                    </div>

                    <SectionCard title="1. General Information">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextF
                          control={control}
                          name="fullName"
                          label="Full name *"
                          placeholder="Your full name"
                        />
                        <TextF
                          control={control}
                          name="email"
                          label="Email *"
                          type="email"
                          placeholder="you@example.com"
                        />
                      </div>
                      <TextF
                        control={control}
                        name="address"
                        label="Street address *"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextF control={control} name="city" label="City *" />
                        <TextF control={control} name="state" label="State *" />
                        <TextF control={control} name="zip" label="ZIP *" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextF
                          control={control}
                          name="cellPhone"
                          label="Cell phone *"
                        />
                        <TextF
                          control={control}
                          name="daytimePhone"
                          label="Daytime phone"
                          placeholder="Optional"
                        />
                        <TextF
                          control={control}
                          name="eveningPhone"
                          label="Evening phone"
                          placeholder="Optional"
                        />
                      </div>
                      <AreaF
                        control={control}
                        name="recoveryConnection"
                        label="Do you have a personal connection to the recovery community? (optional)"
                        placeholder="Feel free to share as much or as little as you like"
                      />
                      <AreaF
                        control={control}
                        name="howHeard"
                        label="How did you learn about Greater Hope Service Dogs and our puppy raiser program? *"
                      />
                      <AreaF
                        control={control}
                        name="whyRaiser"
                        label="Why do you want to be a puppy raiser for a service dog in training? *"
                        rows={4}
                      />
                    </SectionCard>

                    <SectionCard
                      title="2. Handlers & Household"
                      description="Who will be the primary people working with the dog?"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextF
                          control={control}
                          name="primaryHandlerName"
                          label="Primary handler name *"
                        />
                        <TextF
                          control={control}
                          name="primaryHandlerAge"
                          label="Age *"
                        />
                        <TextF
                          control={control}
                          name="primaryHandlerOccupation"
                          label="Occupation *"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextF
                          control={control}
                          name="secondaryHandlerName"
                          label="Secondary handler name"
                          placeholder="Optional"
                        />
                        <TextF
                          control={control}
                          name="secondaryHandlerAge"
                          label="Age"
                          placeholder="Optional"
                        />
                        <TextF
                          control={control}
                          name="secondaryHandlerOccupation"
                          label="Occupation"
                          placeholder="Optional"
                        />
                      </div>
                      <AreaF
                        control={control}
                        name="householdMembers"
                        label="Other members of your household (name, age, occupation)"
                        placeholder="Optional"
                      />
                    </SectionCard>

                    <SectionCard title="3. Typical Day & Schedule">
                      <AreaF
                        control={control}
                        name="typicalWeekday"
                        label="Please describe how you spend a typical weekday. *"
                        rows={4}
                      />
                      <AreaF
                        control={control}
                        name="typicalWeekend"
                        label="Please describe how you spend a typical weekend. *"
                        rows={4}
                      />
                    </SectionCard>

                    <SectionCard title="4. Dog Experience & Current Pets">
                      <YesNoF
                        control={control}
                        name="ownedDogsBefore"
                        label="Have you ever owned a dog? *"
                      />
                      {ownedDogsBefore === "yes" && (
                        <AreaF
                          control={control}
                          name="ownedDogsDetails"
                          label="When, and what breed(s)?"
                        />
                      )}
                      <YesNoF
                        control={control}
                        name="raisedPuppy"
                        label="Have you ever raised a puppy?"
                      />
                      {raisedPuppy === "yes" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <YesNoF
                            control={control}
                            name="pottyTrainedPuppy"
                            label="Have you potty trained a puppy?"
                          />
                          <YesNoF
                            control={control}
                            name="crateTrainedPuppy"
                            label="Have you crate trained a puppy?"
                          />
                        </div>
                      )}
                      <YesNoF
                        control={control}
                        name="currentPets"
                        label="Do you currently own any pets? *"
                      />
                      {currentPets === "yes" && (
                        <div className="space-y-6">
                          <AreaF
                            control={control}
                            name="currentPetsDetails"
                            label="What type(s), breed(s), and age(s)?"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <YesNoF
                              control={control}
                              name="petsSpayedNeutered"
                              label="Are your pets spayed or neutered?"
                            />
                            <YesNoF
                              control={control}
                              name="petsVaccinationProof"
                              label="Can you provide proof of vaccinations?"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TextF
                              control={control}
                              name="vetName"
                              label="Veterinarian name"
                            />
                            <TextF
                              control={control}
                              name="vetPhone"
                              label="Veterinarian phone"
                            />
                          </div>
                          <YesNoF
                            control={control}
                            name="petProblemBehaviors"
                            label="Does your dog have any behavior that could be problematic with a new puppy in the house?"
                          />
                          {petProblemBehaviors === "yes" && (
                            <AreaF
                              control={control}
                              name="petProblemBehaviorsDetails"
                              label="Please describe"
                            />
                          )}
                        </div>
                      )}
                      <YesNoF
                        control={control}
                        name="trainingClasses"
                        label="Have you ever attended dog training classes?"
                      />
                      {trainingClasses === "yes" && (
                        <AreaF
                          control={control}
                          name="trainingClassesDetails"
                          label="What kind? (obedience, agility, show, search and rescue, assistance dog puppy raising, other)"
                        />
                      )}
                    </SectionCard>

                    <SectionCard title="5. Your Home">
                      <FormField
                        control={control}
                        name="residenceType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              In what type of residence do you live? *
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value ?? ""}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose one" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="house">House</SelectItem>
                                <SelectItem value="townhouse">
                                  Townhouse
                                </SelectItem>
                                <SelectItem value="apartment_condo">
                                  Apartment / Condo
                                </SelectItem>
                                <SelectItem value="dorm">Dorm</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {residenceType === "other" && (
                        <TextF
                          control={control}
                          name="residenceTypeOther"
                          label="Please describe your residence"
                        />
                      )}
                      <FormField
                        control={control}
                        name="ownOrRent"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel>
                              Do you own or rent your residence? *
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value ?? ""}
                                className="flex gap-6"
                              >
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="own" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    Own
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="rent" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    Rent
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {ownOrRent === "rent" && (
                        <div className="space-y-6">
                          <YesNoF
                            control={control}
                            name="landlordPermission"
                            label="Can you provide written verification that dogs are permitted in your residence?"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TextF
                              control={control}
                              name="landlordName"
                              label="Landlord's name"
                            />
                            <TextF
                              control={control}
                              name="landlordPhone"
                              label="Landlord's phone"
                            />
                          </div>
                        </div>
                      )}
                      <YesNoF
                        control={control}
                        name="hasYard"
                        label="Do you have access to a yard at your residence? *"
                      />
                      {hasYard === "yes" && (
                        <div className="space-y-6">
                          <YesNoF
                            control={control}
                            name="yardFenced"
                            label="Is the yard fully fenced in?"
                          />
                          {yardFenced === "yes" && (
                            <TextF
                              control={control}
                              name="fenceDescription"
                              label="What type of fence, and how tall?"
                            />
                          )}
                        </div>
                      )}
                      <YesNoF
                        control={control}
                        name="hasPool"
                        label="Do you have a pool at your residence? *"
                      />
                      {hasPool === "yes" && (
                        <YesNoF
                          control={control}
                          name="poolFenced"
                          label="Is the pool fenced?"
                        />
                      )}
                      <AreaF
                        control={control}
                        name="neighborhoodDogInteraction"
                        label="How much interaction do you anticipate the puppy will have with other dogs in your neighborhood?"
                      />
                    </SectionCard>

                    <SectionCard title="6. Life with a Puppy">
                      <YesNoF
                        control={control}
                        name="acceptDamageRisk"
                        label="Do you accept that Greater Hope Service Dogs will not be responsible for any damage by the dog to your house, household items, yard, car, school, or workplace? *"
                      />
                      <YesNoF
                        control={control}
                        name="acknowledgeAccidents"
                        label="Do you acknowledge that the puppy may have toileting accidents in your home? *"
                      />
                      <FormField
                        control={control}
                        name="planWorkSchool"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Do you plan to take the dog to work or school with
                              you? *
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value ?? ""}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose one" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="work">
                                  Yes, to work
                                </SelectItem>
                                <SelectItem value="school">
                                  Yes, to school
                                </SelectItem>
                                <SelectItem value="both">
                                  Yes, to both
                                </SelectItem>
                                <SelectItem value="neither">No</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {planWorkSchool && planWorkSchool !== "neither" && (
                        <div className="space-y-6">
                          <YesNoF
                            control={control}
                            name="workSchoolPermission"
                            label="Do you have permission to take the dog to work or school?"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TextF
                              control={control}
                              name="workSchoolDaysHours"
                              label="How many days a week and hours a day?"
                              placeholder="e.g. 3 days a week, 6 hours a day"
                            />
                            <TextF
                              control={control}
                              name="workSchoolLocation"
                              label="Where do you work or attend school?"
                            />
                          </div>
                          <AreaF
                            control={control}
                            name="workSchoolEnvironment"
                            label="What kind of environment would the dog be exposed to there?"
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextF
                          control={control}
                          name="aloneHours"
                          label="How many hours would the dog be alone on a typical day? *"
                        />
                        <TextF
                          control={control}
                          name="aloneLocation"
                          label="If left alone at home, where will the dog stay?"
                        />
                      </div>
                      <AreaF
                        control={control}
                        name="situationsNotTake"
                        label="In what situation(s) would you not take the dog with you?"
                      />
                      <TextF
                        control={control}
                        name="sleepingArrangements"
                        label="Where in the house will the dog sleep at night? *"
                      />
                      <AreaF
                        control={control}
                        name="exercisePlan"
                        label="How will you exercise the dog, and how often? *"
                      />
                      <YesNoF
                        control={control}
                        name="publicOutingsCommitment"
                        label="Are you committed and willing to take the dog on public outings, such as the grocery store, mall, or restaurants? You are responsible for the safe, positive exposure of the dog to public places and experiences. *"
                      />
                      {publicOutings === "no" && (
                        <AreaF
                          control={control}
                          name="publicOutingsExplain"
                          label="Please explain"
                        />
                      )}
                      <YesNoF
                        control={control}
                        name="smokes"
                        label="Do you smoke (cigarettes or marijuana)? *"
                      />
                      {smokes === "yes" && (
                        <YesNoF
                          control={control}
                          name="refrainFromSmoking"
                          label="Can you commit to refraining from smoking near the dog, including in your home?"
                        />
                      )}
                      <YesNoF
                        control={control}
                        name="commitTrainingSessions"
                        label="Can you commit to attending our training sessions? (Frequency and length may vary.) *"
                      />
                      <YesNoF
                        control={control}
                        name="commitReports"
                        label="Can you commit to filling out reports on the dog's behavior and experiences? *"
                      />
                      <YesNoF
                        control={control}
                        name="consentHomeVisits"
                        label="Do you consent to pre-announced visits to your home by a Greater Hope Service Dogs representative before and during the time the dog is in your care? *"
                      />
                      <YesNoF
                        control={control}
                        name="anyGenderBreed"
                        label="Our service dogs in training may be male or female and of various breeds, and may not be altered while in your care. Are you willing to raise a dog of any gender or breed? *"
                      />
                      {anyGenderBreed === "no" && (
                        <AreaF
                          control={control}
                          name="anyGenderBreedExplain"
                          label="Please explain"
                        />
                      )}
                    </SectionCard>

                    <SectionCard
                      title="7. Reference"
                      description="Please provide contact information for someone we can contact as a reference."
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextF
                          control={control}
                          name="referenceName"
                          label="Name *"
                        />
                        <TextF
                          control={control}
                          name="referenceRelationship"
                          label="Relationship *"
                        />
                        <TextF
                          control={control}
                          name="referencePhone"
                          label="Phone *"
                        />
                        <TextF
                          control={control}
                          name="referenceEmail"
                          label="Email"
                          placeholder="Optional"
                        />
                      </div>
                      <TextF
                        control={control}
                        name="referenceAddress"
                        label="Address"
                        placeholder="Optional"
                      />
                    </SectionCard>

                    <SectionCard
                      title="8. Volunteer Agreement"
                      description="Please read and acknowledge each statement below."
                    >
                      <FormField
                        control={control}
                        name="agreeEmergencyCare"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1">
                              <FormLabel className="font-normal leading-snug">
                                I authorize Greater Hope Service Dogs to seek
                                emergency medical treatment for the dog in case
                                of an accident, injury, or illness. *
                              </FormLabel>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="agreeRiskAcknowledgment"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1">
                              <FormLabel className="font-normal leading-snug">
                                I understand that working with dogs involves
                                risks, including possible injury, illness, and
                                damage to property, and I choose to volunteer
                                with Greater Hope Service Dogs of my own free
                                will with full knowledge and acceptance of those
                                risks. I understand that as an unpaid volunteer
                                I am not covered by workers' compensation. *
                              </FormLabel>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="agreeTruthful"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1">
                              <FormLabel className="font-normal leading-snug">
                                I acknowledge that the information on this
                                application is true and correct. I understand
                                that any misrepresentation of facts may result
                                in the removal of the dog from my home. *
                              </FormLabel>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                      <TextF
                        control={control}
                        name="signatureName"
                        label="Type your full name as your signature *"
                        placeholder="Your full legal name"
                      />
                      <p className="text-xs text-muted-foreground">
                        Typing your name above acts as your electronic
                        signature, dated the day you submit this application.
                      </p>
                    </SectionCard>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={submitApplication.isPending}
                      >
                        {submitApplication.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Submit Application
                      </Button>
                    </div>
                  </form>
                </Form>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
